import * as pdfjsLib from 'pdfjs-dist'
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'
import type { ApiService } from '@/services/api'
import type { PDFFile } from '@/types'
import { logger } from '@/utils/logger'

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'

/**
 * Handles core PDF rendering functionality
 * Extracted from the monolithic PDFViewer class
 */
export class PDFRenderer {
  private canvas: HTMLCanvasElement | null = null
  private context: CanvasRenderingContext2D | null = null
  private container: HTMLElement
  private apiService: ApiService
  // PDF state
  private pdfDocument: PDFDocumentProxy | null = null
  private currentFile: PDFFile | null = null
  private currentPage: number = 1
  private scale: number = 1.2
  private isLoading: boolean = false
  private isRendering: boolean = false
  
  // Page caching for performance
  private pageCache: Map<number, PDFPageProxy> = new Map()
  private loadedPages: Set<number> = new Set()
  
  // Callbacks
  private onScaleChanged?: (scale: number) => void
  
  constructor(container: HTMLElement, apiService: ApiService) {
    this.container = container
    this.apiService = apiService
    this.init()
  }
  
  private init(): void {
    // Get canvas and context
    this.canvas = this.container.querySelector('#pdf-canvas') as HTMLCanvasElement
    if (this.canvas) {
      this.context = this.canvas.getContext('2d')
    }
    
    logger.pdf('PDFRenderer initialized')
  }
  
  /**
   * Load a PDF document
   */
  async loadPDF(file: PDFFile, retryCount: number = 0): Promise<void> {
    const maxRetries = 3
    
    if (this.isLoading) {
      logger.pdf('PDF already loading, skipping duplicate load')
      return
    }
    
    try {
      logger.pdf('Loading PDF:', file.filename, 'ID:', file.id)
      
      this.isLoading = true
      this.currentFile = file
      this.currentPage = 1
      
      // Clear caches
      this.pageCache.clear()
      this.loadedPages.clear()
      
      this.showLoading(true)
      this.updateLoadingProgress('Loading PDF...', 10)
      
      // Test if PDF.js is available
      if (typeof pdfjsLib === 'undefined') {
        throw new Error('PDF.js library not loaded. Please check if PDF.js script is included.')
      }
      
      this.updateLoadingProgress('Downloading PDF...', 30)
      
      // Download PDF using authenticated API service
      const pdfArrayBuffer = await this.apiService.downloadDocumentAsArrayBuffer(file.id)
      this.updateLoadingProgress('Parsing PDF...', 60)
      
      // Load PDF document
      this.pdfDocument = await pdfjsLib.getDocument(pdfArrayBuffer).promise
      
      logger.pdf('PDF document loaded, pages:', this.pdfDocument.numPages)
      
      this.updateLoadingProgress('Rendering first page...', 80)
      
      // Render first page
      await this.renderPage(1)
      
      this.updateLoadingProgress('Complete!', 100)
      
      // Update UI
      this.updatePageInfo()
      this.updateZoomDisplay()
      this.setupEventListeners()
      
      this.showLoading(false)
      this.isLoading = false
      
      logger.pdf('PDF loading completed successfully')
      
    } catch (error) {
      this.isLoading = false
      logger.error('Failed to load PDF:', error)
      
      if (retryCount < maxRetries) {
        logger.pdf(`Retrying PDF load (attempt ${retryCount + 1}/${maxRetries})`)
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)))
        return this.loadPDF(file, retryCount + 1)
      }
      
      throw error
    }
  }
  
  /**
   * Render a specific page
   */
  async renderPage(pageNumber: number): Promise<void> {
    if (!this.pdfDocument || !this.canvas || !this.context) {
      logger.error('Cannot render page: PDF document, canvas, or context not available')
      return
    }
    
    if (this.isRendering) {
      logger.pdf('Already rendering, skipping duplicate render')
      return
    }
    
    try {
      this.isRendering = true
      this.currentPage = pageNumber
      
      logger.pdf(`Rendering page ${pageNumber}`)
      
      // Get page from cache or load it
      let page: PDFPageProxy
      if (this.pageCache.has(pageNumber)) {
        page = this.pageCache.get(pageNumber)!
        logger.pdf(`Using cached page ${pageNumber}`)
      } else {
        page = await this.pdfDocument.getPage(pageNumber)
        this.pageCache.set(pageNumber, page)
        this.loadedPages.add(pageNumber)
        logger.pdf(`Loaded and cached page ${pageNumber}`)
      }
      
      // Calculate viewport
      const viewport = page.getViewport({ scale: this.scale })
      
      // Set canvas dimensions
      this.canvas.width = viewport.width
      this.canvas.height = viewport.height
      
      // Clear canvas
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
      
      // Render page
      const renderContext = {
        canvasContext: this.context,
        viewport: viewport
      }
      
      await page.render(renderContext).promise
      
      logger.pdf(`Page ${pageNumber} rendered successfully`)
      
    } catch (error) {
      logger.error(`Failed to render page ${pageNumber}:`, error)
      throw error
    } finally {
      this.isRendering = false
    }
  }
  
  /**
   * Zoom at a specific point
   */
  async zoomAtPoint(delta: number, mouseX: number, mouseY: number): Promise<void> {
    if (!this.canvas || !this.pdfDocument) return
    
    const oldScale = this.scale
    const newScale = Math.max(0.5, Math.min(5.0, this.scale + delta * 0.1))
    
    if (newScale === oldScale) return
    
    // Calculate zoom center relative to canvas
    const rect = this.canvas.getBoundingClientRect()
    const centerX = (mouseX - rect.left) / oldScale
    const centerY = (mouseY - rect.top) / oldScale
    
    this.scale = newScale
    
    // Notify scale change
    this.onScaleChanged?.(this.scale)
    
    // Re-render current page
    await this.renderPage(this.currentPage)
    
    // Adjust scroll position to maintain zoom center
    const container = this.canvas.parentElement
    if (container) {
      const newCenterX = centerX * newScale
      const newCenterY = centerY * newScale
      
      container.scrollLeft = newCenterX - (mouseX - rect.left)
      container.scrollTop = newCenterY - (mouseY - rect.top)
    }
    
    this.updateZoomDisplay()
  }
  
  /**
   * Set zoom level
   */
  async setZoom(scale: number): Promise<void> {
    this.scale = Math.max(0.5, Math.min(5.0, scale))
    
    // Notify scale change
    this.onScaleChanged?.(this.scale)
    
    await this.renderPage(this.currentPage)
    this.updateZoomDisplay()
  }
  
  /**
   * Fit page to width
   */
  async fitToWidth(): Promise<void> {
    if (!this.canvas || !this.pdfDocument) return
    
    const container = this.canvas.parentElement
    if (!container) return
    
    const containerWidth = container.clientWidth - 40 // Account for padding
    const page = await this.pdfDocument.getPage(this.currentPage)
    const viewport = page.getViewport({ scale: 1.0 })
    
    this.scale = containerWidth / viewport.width
    
    // Notify scale change
    this.onScaleChanged?.(this.scale)
    
    await this.renderPage(this.currentPage)
    this.updateZoomDisplay()
  }
  
  /**
   * Navigate to next page
   */
  async nextPage(): Promise<void> {
    if (!this.pdfDocument || this.currentPage >= this.pdfDocument.numPages) return
    
    const previousPage = this.currentPage
    await this.renderPage(this.currentPage + 1)
    this.updatePageInfo()
    
    // Notify selection manager if page actually changed
    if (previousPage !== this.currentPage) {
      console.log(`📄 Page changed from ${previousPage} to ${this.currentPage}, notifying selection manager`)
      this.notifyPageChanged()
    }
  }
  
  /**
   * Navigate to previous page
   */
  async prevPage(): Promise<void> {
    if (this.currentPage <= 1) return
    
    const previousPage = this.currentPage
    await this.renderPage(this.currentPage - 1)
    this.updatePageInfo()
    
    // Notify selection manager if page actually changed
    if (previousPage !== this.currentPage) {
      console.log(`📄 Page changed from ${previousPage} to ${this.currentPage}, notifying selection manager`)
      this.notifyPageChanged()
    }
  }
  
  /**
   * Navigate to specific page
   */
  async goToPage(pageNumber: number): Promise<void> {
    if (!this.pdfDocument || pageNumber < 1 || pageNumber > this.pdfDocument.numPages) return
    
    const previousPage = this.currentPage
    await this.renderPage(pageNumber)
    this.updatePageInfo()
    
    // Notify selection manager if page actually changed
    if (previousPage !== pageNumber) {
      console.log(`📄 Page changed from ${previousPage} to ${pageNumber}, notifying selection manager`)
      this.notifyPageChanged()
    }
  }
  
  /**
   * Get current page number
   */
  getCurrentPage(): number {
    return this.currentPage
  }

  /**
   * Notify that the page has changed
   */
  private notifyPageChanged(): void {
    // Try to notify the selection manager via the global PDF viewer
    if ((window as any).pdfViewerRefactored && (window as any).pdfViewerRefactored.ensureSelectionManager) {
      try {
        (window as any).pdfViewerRefactored.ensureSelectionManager().onPageChanged()
      } catch (error) {
        console.warn('Failed to notify selection manager of page change:', error)
      }
    }
  }
  
  /**
   * Get total number of pages
   */
  getTotalPages(): number {
    return this.pdfDocument?.numPages || 0
  }
  
  /**
   * Get current scale
   */
  getScale(): number {
    return this.scale
  }
  
  /**
   * Set callback for scale changes
   */
  setOnScaleChanged(callback: (scale: number) => void): void {
    this.onScaleChanged = callback
  }
  
  /**
   * Get current PDF file
   */
  getCurrentFile(): PDFFile | null {
    return this.currentFile
  }
  
  /**
   * Get PDF document
   */
  getPDFDocument(): PDFDocumentProxy | null {
    return this.pdfDocument
  }
  
  /**
   * Check if currently loading
   */
  getIsLoading(): boolean {
    return this.isLoading
  }
  
  /**
   * Check if currently rendering
   */
  getIsRendering(): boolean {
    return this.isRendering
  }
  
  /**
   * Clear all state and caches
   */
  clearState(): void {
    this.pdfDocument = null
    this.currentFile = null
    this.currentPage = 1
    this.scale = 1.2
    this.isLoading = false
    this.isRendering = false
    this.pageCache.clear()
    this.loadedPages.clear()
    
    if (this.canvas && this.context) {
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
    }
  }
  
  /**
   * Setup event listeners for PDF controls
   */
  private setupEventListeners(): void {
    // Previous page button
    const prevBtn = this.container.querySelector('#prev-page') as HTMLButtonElement
    if (prevBtn) {
      prevBtn.onclick = () => this.prevPage()
      prevBtn.disabled = this.currentPage <= 1
    }
    
    // Next page button
    const nextBtn = this.container.querySelector('#next-page') as HTMLButtonElement
    if (nextBtn) {
      nextBtn.onclick = () => this.nextPage()
      nextBtn.disabled = this.currentPage >= this.getTotalPages()
    }
    
    // Page input
    const pageInput = this.container.querySelector('#page-input') as HTMLInputElement
    if (pageInput) {
      pageInput.onchange = () => {
        const pageNum = parseInt(pageInput.value)
        if (pageNum && pageNum !== this.currentPage) {
          this.goToPage(pageNum)
        }
      }
    }
    
    // Zoom controls
    const zoomInBtn = this.container.querySelector('#zoom-in') as HTMLButtonElement
    if (zoomInBtn) {
      zoomInBtn.onclick = () => this.setZoom(this.scale + 0.2)
    }
    
    const zoomOutBtn = this.container.querySelector('#zoom-out') as HTMLButtonElement
    if (zoomOutBtn) {
      zoomOutBtn.onclick = () => this.setZoom(this.scale - 0.2)
    }
    
    const fitWidthBtn = this.container.querySelector('#fit-width') as HTMLButtonElement
    if (fitWidthBtn) {
      fitWidthBtn.onclick = () => this.fitToWidth()
    }
    
    // Canvas wheel zoom (only when Ctrl/Cmd is pressed)
    if (this.canvas) {
      this.canvas.addEventListener('wheel', (e) => {
        // Only handle zoom if Ctrl/Cmd is pressed, otherwise allow normal scrolling
        if (!e.ctrlKey && !e.metaKey) {
          return
        }
        
        e.preventDefault()
        const delta = e.deltaY > 0 ? -0.1 : 0.1
        this.zoomAtPoint(delta, e.clientX, e.clientY)
      })
    }
  }
  
  /**
   * Update page information display
   */
  private updatePageInfo(): void {
    const pageInput = this.container.querySelector('#page-input') as HTMLInputElement
    const pageInfo = this.container.querySelector('#page-info') as HTMLElement
    const prevBtn = this.container.querySelector('#prev-page') as HTMLButtonElement
    const nextBtn = this.container.querySelector('#next-page') as HTMLButtonElement
    
    if (pageInput) {
      pageInput.value = this.currentPage.toString()
      pageInput.max = this.getTotalPages().toString()
    }
    
    if (pageInfo) {
      pageInfo.textContent = `of ${this.getTotalPages()}`
    }
    
    if (prevBtn) {
      prevBtn.disabled = this.currentPage <= 1
    }
    
    if (nextBtn) {
      nextBtn.disabled = this.currentPage >= this.getTotalPages()
    }
  }
  
  /**
   * Update zoom level display
   */
  private updateZoomDisplay(): void {
    const zoomLevel = this.container.querySelector('#zoom-level') as HTMLElement
    if (zoomLevel) {
      zoomLevel.textContent = `${Math.round(this.scale * 100)}%`
    }
  }
  
  /**
   * Show/hide loading indicator
   */
  private showLoading(show: boolean): void {
    const loadingEl = this.container.querySelector('#pdf-loading') as HTMLElement
    const contentEl = this.container.querySelector('#pdf-content') as HTMLElement
    
    if (loadingEl) {
      loadingEl.style.display = show ? 'flex' : 'none'
    }
    
    if (contentEl) {
      contentEl.classList.toggle('hidden', show)
    }
  }
  
  /**
   * Update loading progress
   */
  private updateLoadingProgress(message: string, progress: number): void {
    const loadingEl = this.container.querySelector('#pdf-loading') as HTMLElement
    if (loadingEl) {
      const messageEl = loadingEl.querySelector('.text-gray-600')
      if (messageEl) {
        messageEl.textContent = message
      }
    }
    
    logger.pdf(`Loading progress: ${progress}% - ${message}`)
  }
}
